import React from "react";
import { useNavigate } from "react-router-dom";

import { Container, Icon, Modal } from "semantic-ui-react";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { NAV } from "../../navLabels";
import { useGetAllowancesQuery } from "./allowancesApiSlice";
import { NotesList, gradedNotes } from "./modeNotes";

/**
 * Entry point for 가상 고객과 롤플레이: 사전 연습 (practice, never graded) or
 * 롤플레이 (graded). Both lead to a persona choice.
 *
 * Copy and structure are the client's, from UI UX_수정요청서_v1.0 requests 3-5. Each box's
 * notice (유의사항, incl. the time limits) is on the customer-choice screen it opens
 * (modeNotes.js), which a trainee still passes before a session starts.
 */

const MODES = [
  {
    key: "practice",
    icon: "clipboard outline",
    title: "사전 연습하기",
    // What the mode is and what to know, in one line: condensed from its 유의사항
    // (modeNotes.js), which the next screen shows in full.
    desc: "평가 전에 자유롭게 연습할 수 있습니다. 점수는 기록되지 않으며, 최대 40분(누적)까지 이용할 수 있습니다.",
    to: "/simulation/practice",
    accent: "#34C759",
    // 유의사항 (the client's notice) lives on the screen this opens: see modeNotes.js.
  },
  {
    key: "graded",
    icon: "clipboard check",
    title: "롤플레이 평가 응시",
    // Leads with the quota: with 1 attempt, it is the thing to know before choosing. Same
    // wording rule as the 유의사항 line in modeNotes.js; exempt users (admins) get none.
    desc: (graded) =>
      `${graded ? `본 평가는 ${graded.attempts_total === 1 ? "1회만" : `${graded.attempts_total}회까지`} 응시할 수 있으며, 결과가` : "결과가"} 평가 점수로 기록됩니다. 약 15~20분 소요되며, 25분 안에 마쳐야 합니다.`,
    to: "/customerpersonascards",
    accent: "#007AFF",
  },
];

/**
 * Turns the API's allowance payload into what a box needs to render: a balance line, and
 * whether entry is blocked.
 *
 * Practice and graded differ deliberately (#85, restated in #112): a partial practice
 * balance is still worth using, so three minutes left means a three-minute session rather
 * than a refusal. Only exactly zero blocks. A spent graded quota has nothing to offer, so
 * any exhaustion blocks.
 */
const balanceFor = (mode, allowances) => {
  if (!allowances) return null;
  const data = allowances[mode.key];
  if (!data) return null; // no limit for this user (graded quota exempts admins)

  if (mode.key === "practice") {
    return {
      label: `남은 시간 ${data.minutes_remaining}분 / ${data.minutes_total}분`,
      blocked: data.exhausted,
      blockedText: "사전 연습 시간을 모두 사용했습니다.",
      resetsAt: data.resets_at,
    };
  }
  // Graded: the remaining count (shown since 2026-10-20; it was hidden at launch, #229).
  return {
    label: `남은 횟수 ${data.attempts_remaining}회 / ${data.attempts_total}회`,
    blocked: data.exhausted,
    blockedText: "롤플레이 횟수를 모두 사용했습니다.",
    resetsAt: data.resets_at,
  };
};

const SessionModeSelect = () => {
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  // Advisory only. The socket still refuses an exhausted budget, so a failed request here
  // must not stop a trainee entering — it just means no balance is shown.
  const { data: allowances } = useGetAllowancesQuery();
  // Graded attempts are scarce (1 by default), so entering 롤플레이 asks first and points at
  // 사전 연습. Client copy, 2026-10-20. Exempt users (no `graded` key) go straight in.
  const [gradedConfirmOpen, setGradedConfirmOpen] = React.useState(false);
  const graded = allowances?.graded;

  // Both modes now lead to a persona choice, so this screen no longer creates sessions
  // and does not need the persona list — it only routes.
  return (
    // 50px is the navbar spacer Layout renders above every page — subtracting it keeps
    // the page exactly one viewport tall instead of forcing a 50px scrollbar.
    <div
      style={{
        backgroundColor: "#f9fafb",
        minHeight: isMobile ? "calc(100dvh - 50px)" : "calc(100vh - 50px)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          padding: "16px 0",
          flexShrink: 0,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(0,0,0,0.05)",
        }}
        className="page-header-band"
      >
        {/* Semantic's Container, the same as 롤플레이's header uses. A flat 24px
            gutter started the title at x=24 on desktop while that page started at x=237,
            so the two screens in the same flow looked like different apps. Container is
            safe *here* because it has no inline width:100% — that combination is what
            overflowed the card grids below (see the comments there). */}
        <Container>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div className="header-icon-desktop-only"
              style={{
                width: "40px",
                flexShrink: 0, // stays a circle when the text beside it wraps
                height: "40px",
                borderRadius: "50%",
                backgroundColor: "#F2F2F7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon name="conversation" style={{ margin: 0, color: "#007AFF", fontSize: "18px" }} />
            </div>
            <div>
              <h2 style={{ fontSize: "17px", fontWeight: "600", margin: 0, color: "#1d1d1f" }}>
                {NAV.ROLEPLAY}
              </h2>
              <p style={{ fontSize: "13px", color: "#86868b", margin: 0 }}>
                진행 방식을 선택해 주세요.
              </p>
            </div>
          </div>
        </Container>
      </div>

      {/* flex:1 + centred, so the boxes sit in the middle of the space left below the
          header rather than hugging the top. The bottom bias is desktop-only: on mobile
          the boxes stack and already fill the screen, and the resulting scrollbar eats the
          right-hand gutter, making the left margin look wider than the right. */}
      <div
        style={{
          flex: 1,
          display: "flex",
          // Top-aligned, as on every other trainee page (it used to centre the cards on
          // desktop, 136px below the header).
          alignItems: "flex-start",
          padding: isMobile ? "0 0 14px" : "0 0 40px",
        }}
      >
        {/* Plain div, not Semantic's Container: `.ui.container` carries a mobile rule
            (margin: 0 1em) that combines with width:100% to overflow by 2em and shove the
            content right — the boxes ran off the right edge on narrow screens. The app
            globally overrides .ui.container's max-width to 100% anyway, so it was
            contributing nothing but that bug. */}
        {/* 14px on phones: the gutter of the header above and of 제품 지식 퀴즈 / 대화 내역. */}
        <div style={{ width: "100%", padding: isMobile ? "0 14px" : "0 24px", display: "flex", flexDirection: "column" }}>
          {/* Shared by both modes, so stated once above them: speaker audio leaking back
              into the mic is transcribed as the trainee or cuts the customer off
              (2026-10-20). Browser echo cancellation catches most of it; earphones remove it. */}
          <div
            style={{
              width: "100%",
              maxWidth: "920px",
              margin: isMobile ? "0 auto 10px" : "0 auto 20px",
              padding: "10px 14px",
              borderRadius: "12px",
              backgroundColor: "#EAF2FF",
              color: "#0B4FB3",
              fontSize: "13px",
              fontWeight: 600,
              display: "flex",
              gap: "8px",
              alignItems: "flex-start",
            }}
          >
            <Icon name="headphones" style={{ margin: 0 }} />
            <span>
              이어폰 사용을 권장합니다.
              <span style={{ fontWeight: 400, display: "block" }}>
                스피커 소리가 마이크에 다시 들어가면 AI가 직원의 말로 잘못 인식할 수 있습니다.
              </span>
            </span>
          </div>
          {/* The cap lives here, not on Container: a global
              `.ui.container { max-width: 100% !important }` makes every container in the
              app full-bleed and beats any inline max-width. */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              // Phones: both cards as tall as the taller one's content. (Filling the screen
              // in halves left them mostly empty once 유의사항 moved to the next screens.)
              gridAutoRows: isMobile ? "1fr" : undefined,
              gap: isMobile ? "12px" : "24px",
              width: "100%",
              maxWidth: "920px",
              margin: "0 auto",
            }}
          >
            {MODES.map((mode) => {
              const balance = balanceFor(mode, allowances);
              const blocked = !!balance?.blocked;
              // 사전 연습 is the blue card and 평가 the white one, as 연습 모드 / 실전 테스트 on
              // 제품 지식 퀴즈.
              const primary = mode.key === "practice";
              const restShadow = primary ? "0 8px 24px rgba(0,122,255,0.2)" : "0 2px 10px rgba(0,0,0,0.03)";
              const enter = () => {
                if (blocked) return;
                if (mode.key === "graded" && graded) setGradedConfirmOpen(true);
                else navigate(mode.to);
              };
              return (
              <div
                key={mode.key}
                role="button"
                tabIndex={blocked ? -1 : 0}
                aria-disabled={blocked}
                onClick={enter}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    enter();
                  }
                }}
                style={{
                  cursor: blocked ? "not-allowed" : "pointer",
                  opacity: blocked ? 0.55 : 1,
                  background: primary ? "linear-gradient(135deg, #007AFF 0%, #0055D4 100%)" : "white",
                  color: primary ? "white" : "#1d1d1f",
                  borderRadius: isMobile ? "20px" : "24px",
                  padding: isMobile ? "18px" : "32px 28px",
                  display: "flex",
                  flexDirection: "column",
                  border: primary ? "none" : "1px solid rgba(0,0,0,0.05)",
                  boxShadow: restShadow,
                  transition: "transform 0.15s ease, box-shadow 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  if (blocked) return; // no affordance for something that cannot be clicked
                  e.currentTarget.style.transform = "translateY(-4px)";
                  e.currentTarget.style.boxShadow = primary ? "0 12px 32px rgba(0,122,255,0.3)" : "0 12px 30px rgba(0,0,0,0.08)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "none";
                  e.currentTarget.style.boxShadow = restShadow;
                }}
              >
                {/* Phones: icon beside the title, not on a row of its own. */}
                <div style={{ display: "flex", flexDirection: isMobile ? "row" : "column", alignItems: isMobile ? "center" : "flex-start", gap: isMobile ? "10px" : "16px" }}>
                  <div
                    style={{
                      width: isMobile ? "36px" : "56px",
                      height: isMobile ? "36px" : "56px",
                      borderRadius: "50%",
                      backgroundColor: primary ? "rgba(255,255,255,0.2)" : `${mode.accent}14`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon
                      name={mode.icon}
                      style={{ margin: 0, color: primary ? "white" : mode.accent, fontSize: isMobile ? "17px" : "24px", lineHeight: 1 }}
                    />
                  </div>
                  <h3 style={{ margin: 0, fontSize: isMobile ? "18px" : "20px", fontWeight: 700, color: "inherit" }}>
                    {mode.title}
                  </h3>
                </div>

                <p style={{ margin: isMobile ? "10px 0 0" : "12px 0 0", fontSize: isMobile ? "13.5px" : "14px", lineHeight: 1.55, color: primary ? "rgba(255,255,255,0.9)" : "#424245" }}>
                  {typeof mode.desc === "function" ? mode.desc(graded) : mode.desc}
                </p>

                {/* The trainee's actual balance, which used to arrive only after the socket
                    opened. The static note below still states the rule; this states where
                    they stand against it. */}
                {balance && (
                  <div
                    style={{
                      marginTop: "8px",
                      display: "inline-flex",
                      alignItems: "center",
                      alignSelf: "flex-start",
                      gap: "6px",
                      padding: "5px 10px",
                      borderRadius: "999px",
                      backgroundColor: blocked ? "#FFE5E5" : primary ? "rgba(255,255,255,0.2)" : `${mode.accent}14`,
                      color: blocked ? "#C0392B" : "inherit",
                      fontSize: "12px",
                      fontWeight: 600,
                    }}
                  >
                    <Icon
                      name={blocked ? "ban" : mode.key === "graded" ? "check circle outline" : "clock outline"}
                      style={{ margin: 0, fontSize: "12px", lineHeight: 1 }}
                    />
                    {blocked ? balance.blockedText : balance.label}
                  </div>
                )}
                {blocked && balance.resetsAt && mode.key !== "graded" && (
                  <div style={{ marginTop: "6px", fontSize: "12px", color: primary ? "rgba(255,255,255,0.8)" : "#86868b" }}>
                    {new Date(balance.resetsAt).toLocaleDateString("ko-KR")}에 초기화됩니다.
                  </div>
                )}

                {/* The way in, as on 제품 지식 퀴즈's cards. */}
                {!blocked && (
                  <Icon name="arrow right" style={{ margin: "auto 0 0", paddingTop: "12px", alignSelf: "flex-end", fontSize: "18px", height: "auto", color: primary ? "white" : "#1d1d1f" }} />
                )}
              </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* Semantic's Modal only as the shell (overlay, Esc/click-outside, focus); the inside is
          drawn like the cards above rather than Semantic's grey-bar default. */}
      <Modal
        size="tiny"
        open={gradedConfirmOpen}
        onClose={() => setGradedConfirmOpen(false)}
        style={{ borderRadius: "20px", overflow: "hidden" }}
      >
        <div style={{ padding: isMobile ? "22px 20px 18px" : "28px 28px 22px", backgroundColor: "white" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ width: "36px", height: "36px", borderRadius: "50%", backgroundColor: "#007AFF14", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icon name="clipboard check" style={{ margin: 0, color: "#007AFF", fontSize: "17px", lineHeight: 1 }} />
            </div>
            <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: "#1d1d1f" }}>롤플레이 평가 안내</h3>
          </div>

          <p style={{ margin: "16px 0 0", fontSize: "15px", fontWeight: 600, color: "#1d1d1f", lineHeight: 1.5 }}>
            본 평가는 {graded?.attempts_total === 1 ? "1회만" : `${graded?.attempts_total}회까지`} 응시 가능합니다.
            {graded && graded.attempts_total > 1 && ` (남은 횟수 ${graded.attempts_remaining}회)`}
          </p>
          <div style={{ marginTop: "10px", padding: "8px 12px", borderRadius: "10px", backgroundColor: "#FFF4E5", color: "#8A4B00", fontSize: "13px", fontWeight: 600, display: "flex", gap: "6px", alignItems: "flex-start" }}>
            <Icon name="info circle" style={{ margin: 0 }} />
            평가 시작 전 사전 연습을 통해 충분히 숙지하신 후 응시해 주세요.
          </div>
          {/* The full 유의사항: this confirm is the last stop before committing to an attempt. */}
          <div style={{ marginTop: "14px", fontSize: "13px", fontWeight: 600, color: "#1d1d1f" }}>유의사항</div>
          <NotesList notes={gradedNotes(graded)} style={{ marginTop: "6px", fontSize: "12.5px", lineHeight: 1.55 }} />

          <div style={{ display: "flex", gap: "8px", marginTop: "20px" }}>
            {[
              ["사전 연습하기", "/simulation/practice", { backgroundColor: "#F2F2F7", color: "#1d1d1f" }],
              ["평가 시작", "/customerpersonascards", { backgroundColor: "#007AFF", color: "white" }],
            ].map(([label, to, colors]) => (
              <button
                key={to}
                type="button"
                onClick={() => navigate(to)}
                style={{ flex: 1, height: "46px", border: "none", borderRadius: "999px", fontSize: "15px", fontWeight: 600, cursor: "pointer", fontFamily: "inherit", ...colors }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SessionModeSelect;
